import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '@xing-com/button';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { useSessionStorage } from '@xing-com/crate-hooks-use-web-storage';
import {
  AI_SEARCH_FEEDBACK_SURVEY_LO_SWIMP_PARAM,
  AI_SEARCH_FEEDBACK_SURVEY_URL,
  AI_SEARCH_FEEDBACK_SURVEY_URL_LO_SWIMP,
} from '@xing-com/crate-jobs-constants';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useViewerData } from '@xing-com/hub';
import { mediaConfined, mediaSmallOrTiny } from '@xing-com/layout-tokens';
import { scale150, spaceL } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

import { AiFeedbackCard } from './ai-feedback-card';
import type { FeedbackVariant } from './color-feedback';
import { FeedbackContent } from './feedback-content';
import { useForceShow } from './use-force-show';

const SESSION_STORAGE_KEY = 'ai-feedback-shown';

const SURVEY_HEADLINE: Record<FeedbackVariant, string> = {
  like: 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_LIKE',
  neutral: 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_NEUTRAL',
  dislike: 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_DISLIKE',
};

const SURVEY_COPY: Record<FeedbackVariant, string> = {
  like: 'JOBS_SEARCH_AI_FEEDBACK_COPY_LIKE',
  neutral: 'JOBS_SEARCH_AI_FEEDBACK_COPY_NEUTRAL',
  dislike: 'JOBS_SEARCH_AI_FEEDBACK_COPY_DISLIKE',
};

const SURVEY_REACTION_PARAM: Record<FeedbackVariant, string> = {
  like: 'L',
  neutral: 'N',
  dislike: 'D',
};

type Props = {
  searchId?: string;
  trackingToken?: string;
  isStickyHeader?: boolean;
  isEmptySearch?: boolean;
};

export const AIFeedback: React.FC<Props> = ({
  searchId,
  trackingToken,
  isStickyHeader,
  isEmptySearch,
}) => {
  const [surveyType, setSurveyType] = useState<FeedbackVariant | null>(null);
  const { data } = useViewerData();
  const hashedId = data?.hashedUserId || '0';
  const trackNwt = useJobsNwtTracking();
  const isTrackedRef = useRef(false);

  const { getSearchParameter } = useSearchParameter();
  const showLoggedOutSwimpVersion =
    getSearchParameter(AI_SEARCH_FEEDBACK_SURVEY_LO_SWIMP_PARAM) === '1';

  const isBigScreen = useMatchMedia(mediaConfined);
  const [showInSession, setShowInSession] = useSessionStorage(
    SESSION_STORAGE_KEY,
    true
  );
  const forceShow = useForceShow(Boolean(isStickyHeader || isBigScreen));
  const show = Boolean(
    searchId &&
    trackingToken &&
    showInSession &&
    (isStickyHeader || isBigScreen || forceShow) &&
    !isEmptySearch
  );

  const handleOnClick = (feedback: FeedbackVariant): void => {
    setSurveyType(feedback);
  };

  const handleOnClose = (): void => {
    setShowInSession(false);
  };

  const handleOnEntered = (): void => {
    if (isTrackedRef.current) {
      return;
    }

    trackNwt({
      eventSchema: 'aicomponent',
      page: 'jobs_search/serp',
      event: 'entered_viewport',
      componentName: 'ai_search',
      componentFeedbackLevel: '2',
      trackingToken,
    });

    isTrackedRef.current = true;
  };

  const headline = showLoggedOutSwimpVersion
    ? 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_LO_SWIMP'
    : surveyType
      ? SURVEY_HEADLINE[surveyType]
      : 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_V2';
  const copy = showLoggedOutSwimpVersion
    ? 'JOBS_SEARCH_AI_FEEDBACK_COPY_LO_SWIMP'
    : surveyType
      ? SURVEY_COPY[surveyType]
      : 'JOBS_SEARCH_AI_FEEDBACK_COPY';
  const cta = showLoggedOutSwimpVersion
    ? 'JOBS_SEARCH_AI_FEEDBACK_CTA_LO_SWIMP'
    : 'JOBS_SEARCH_AI_FEEDBACK_CTA';

  const urlParams = new URLSearchParams({
    r: `${hashedId}_${trackingToken}`,
    ...(surveyType && { h: SURVEY_REACTION_PARAM[surveyType] }),
    ...(showLoggedOutSwimpVersion && { hasSelectionScreen: 'true' }),
  });
  const url = showLoggedOutSwimpVersion
    ? AI_SEARCH_FEEDBACK_SURVEY_URL_LO_SWIMP
    : AI_SEARCH_FEEDBACK_SURVEY_URL;
  const surveyUrl = `${url}?${urlParams.toString()}`;

  return (
    <AiFeedbackCard
      show={show}
      onClose={handleOnClose}
      onEntered={handleOnEntered}
    >
      <Title size="large" noMargin $hideOnMobile={!surveyType}>
        <FormattedMessage id={headline} />
      </Title>
      <Text noMargin>
        <FormattedMessage id={copy} />
      </Text>
      {!surveyType && !showLoggedOutSwimpVersion ? (
        <FeedbackContent
          onClick={handleOnClick}
          trackingToken={trackingToken}
        />
      ) : (
        <Button
          variant="secondary"
          href={surveyUrl}
          target="_blank"
          rel="noopener"
          onClick={handleOnClose}
        >
          <FormattedMessage id={cta} />
        </Button>
      )}
    </AiFeedbackCard>
  );
};

const Title = styled(Headline)<{ $hideOnMobile: boolean }>`
  // close button width - card padding, to avoid text going under the close button
  padding-inline: calc(${scale150} - ${spaceL});

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    css`
      @media ${mediaSmallOrTiny} {
        display: none;
      }
    `}
`;

const Text = styled(BodyCopy)`
  // close button width - card padding, to avoid text going under the close button
  padding-inline: calc(${scale150} - ${spaceL});
`;
